import { EventEmitter, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  IBlobInfo,
  IBlobItem,
  IBlobMetadata,
} from 'src/app/multiview/models/interfaces';
import { DomSanitizer } from '@angular/platform-browser';
import { SafeUrl } from '@angular/platform-browser';
@Injectable({
  providedIn: 'root',
})
export class IndexedDbService {
  private dbName = 'blobDB';
  private storeName = 'multimediaStore';
  private db!: IDBDatabase;
  public isReady = false;
  public completed: EventEmitter<any> = new EventEmitter<any>();

  constructor(
    private sanitizer: DomSanitizer,
  ) {
    this.initializeDatabase();
  }

  private initializeDatabase(): void {
    const openDBRequest = indexedDB.open(this.dbName, 1);

    openDBRequest.onupgradeneeded = (event: any) => {
      this.db = event.target.result;
      if (!this.db.objectStoreNames.contains(this.storeName)) {
        this.db.createObjectStore(this.storeName, {
          keyPath: 'id',
          autoIncrement: true,
        });
      }
      this.isReady = true;
      this.completed.emit();
    };

    openDBRequest.onsuccess = (event: any) => {
      this.db = event.target.result;
      this.isReady = true;
      this.completed.emit();
    };
  }

  public saveBlob(
    arrayBuffer: ArrayBuffer,
    type: string,
    timestamp: number
  ): Observable<any> {
    return new Observable((observer) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);

      const blobData = {
        data: arrayBuffer,
        type: type,
        timestamp,
      };
      // console.log("STORING BLOB: ", blobData);
      const request = store.add(blobData);
      // console.log("STORING result: ", request);
      request.onsuccess = () => {
        observer.next(request.result);
        observer.complete();
      };

      request.onerror = (error) => {
        observer.error(error);
      };
    });
  }

  public getBlob(id: string): Observable<Blob> {
    // console.log("GETBLOB ID:", id);

    return new Observable((observer) => {
      const transaction = this.db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const getRequest = store.get(id);

      getRequest.onsuccess = (event: any) => {
        const blobData = event.target.result;
        if (blobData) {
          const blob = new Blob([blobData.data], { type: blobData.type });
          //console.log("BLOB RETRIVED: ", blob);

          observer.next(blob);
          observer.complete();
        } else {
          // observer.next(undefined);
          observer.error('Blob not found');
        }
      };

      getRequest.onerror = (error) => {
        observer.error(error);
      };
    });
  }
  public getBlobItem(id: string): Observable<IBlobItem> {
    return new Observable((observer) => {
      const transaction = this.db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const getRequest = store.get(id);

      getRequest.onsuccess = (event: any) => {
        const blobData = event.target.result;
        if (blobData) {
          const blob = new Blob([blobData.data], { type: blobData.type });
          //console.log("BLOB RETRIVED: ", blob);

          observer.next({
            id,
            blob,
            type: blobData.type,
            timestamp: blobData.timestamp,
          });
          observer.complete();
        } else {
          // observer.next(undefined);
          observer.error('Blob not found');
        }
      };

      getRequest.onerror = (error) => {
        observer.error(error);
      };
    });
  }


  public getBlobUrl(id: string): Observable<{ safeUrl: SafeUrl, objectUrl: string }> {
    return new Observable((observer) => {
      const transaction = this.db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const getRequest = store.get(id);
  
      getRequest.onsuccess = (event: any) => {
        const blobData = event.target.result;
        if (blobData) {
          const blob = new Blob([blobData.data], { type: blobData.type });
  
          // Generamos la URL y la SafeUrl
          const objectUrl = URL.createObjectURL(blob);
          const safeUrl = this.sanitizer.bypassSecurityTrustUrl(objectUrl);
  
          observer.next({ safeUrl, objectUrl }); // Devolvemos ambas URLs
          observer.complete();
        } else {
          observer.error('Blob not found');
        }
      };
  
      getRequest.onerror = (error) => {
        observer.error(error);
      };
    });
  }
  
  public deleteBlob(id: string): Observable<void> {
    return new Observable<void>((observer) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const deleteRequest = store.delete(id);

      deleteRequest.onsuccess = () => {
        observer.next();
        observer.complete();
      };

      deleteRequest.onerror = (error) => {
        observer.error('Error al eliminar el elemento de IndexedDB: ' + error);
      };
    });
  }

  public deleteAllBlob(): Observable<void> {
    return new Observable<void>((observer) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);

      // Usar clear() para eliminar todos los elementos del objectStore
      const clearRequest = store.clear();

      clearRequest.onsuccess = () => {
        // console.log("IndexedDB: Todos los elementos han sido eliminados");
        observer.next();
        observer.complete();
      };

      clearRequest.onerror = (error: any) => {
        console.error('Error al eliminar todos los elementos:', error);
        observer.error(
          'Error al eliminar todos los elementos de IndexedDB: ' + error
        );
      };
    });
  }

  public async cleanOldItems(store: any) {
    // console.log('IndexedDB CLEANER');
    const now = new Date(); // Calcula la fecha actual una vez
    const limite_time = 1 * 5 * 60 * 1000;
    // console.log('IndexedDB Fecha NOW -> ', now.getTime());

    const getAllRequest = store.getAll();
    getAllRequest.onsuccess = () => {
      const items = getAllRequest.result;
      // console.log("IndexedDB Lista de elementos:");
      items.forEach((item: any) => {
        // console.log('IndexedDB -> ', item);
        // Validar si el timestamp del elemento es mayor a 1 hora
        const itemTimestamp = item.timestamp;
        const itemDate = new Date(itemTimestamp);
        // console.log('IndexedDB -> ',itemDate,' blob:',item.id);
        if (now.getTime() - itemDate.getTime() > limite_time) {
          // console.log("IndexedDB Elemento antiguo encontrado y eliminado:");
          // console.log(item);

          // Eliminar el elemento antiguo
          const deleteRequest = store.delete(item.id);
          deleteRequest.onsuccess = () => {
            // console.log("IndexedDB Elemento eliminado con éxito.");
          };
          deleteRequest.onerror = (error: any) => {
            console.error('IndexedDB Error al eliminar el elemento:', error);
          };
        }
      });
    };
  }

  public getAllBlobsMetadata(): Observable<IBlobMetadata> {
    return new Observable((observer) => {
      const transaction = this.db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.openCursor();
  
      request.onsuccess = (event: any) => {
        const cursor = event.target.result;
  
        if (cursor) {
          const blobData = cursor.value;
  
          // Puedes hacer verificación de tamaño aquí si deseas limitar
          const blob = new Blob([blobData.data], { type: blobData.type });
  
          const metadata: IBlobMetadata = {
            id: blobData.id,
            size: blob.size,
            type: blobData.type,
            timestamp: blobData.timestamp,
            eventIds: [], // o ajusta según tu estructura
          };
  
          observer.next(metadata); // Emitimos uno por uno
  
          cursor.continue(); // Continúa al siguiente
        } else {
          observer.complete(); // No hay más blobs
        }
      };
  
      request.onerror = (error) => {
        observer.error(error); // Manejo de error
      };
    });
  }
}
